import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Spinner from '~/Components/Global/Loading/Spinner';
import { useMountEffect } from '~/util';
import { paddleApi, UserApi } from '~/Providers/Api';
import { usePaddleCheckout } from '~/util/usePaddleCheckout';
import { error as showError } from '~/Redux/Modules/Notifications';
import { setUser } from '~/Redux/Modules/User';
import history from '~/Providers/History';
import moment from '~/Providers/Moment';
import SubscriptionPlan from './SubscriptionPlan';

import './ManageSubscription.scss';

const SwapToPaddle = ({
  selectedPlan,
  isFetching,
  account,
  handleSave,
  errorMessage,
  eventId,
  pricingPlans,
  historicalPlans,
  onPlanSelect,
}) => {
  const dispatch = useDispatch();
  const [provisioning, setProvisioning] = useState(false);
  const pollingInterval = useRef(null);
  const [pollingTime, setPollingTime] = useState(10000);

  const { isLoading, openCheckout, closeCheckout, initializePaddle } = usePaddleCheckout({
    onEvent: event => {
      console.log('SwapToPaddle', 'event', event.name, event.data);

      if (event.name === 'checkout.completed') {
        // Show loading state and poll for provisioning status
        setProvisioning(true);
        closeCheckout();
        setPollingTime(1000);
      }
    },
    settings: {
      variant: 'one-page',
    },
  });

  useEffect(() => {
    console.log('SwapToPaddle', 'useEffect', pollingInterval.current);
    pollingInterval.current = setInterval(async () => {
      console.log('SwapToPaddle', 'polling for subscription');
      const { data: apiUser } = await UserApi.fetch();

      const newSubscription = apiUser.subscriptions.find(({ platform, created_at }) => {
        return platform === 'paddle' && moment(created_at).isAfter(moment().subtract(1, 'minutes'));
      });

      if (newSubscription) {
        console.log('SwapToPaddle', 'clearing polling interval');
        dispatch(setUser(apiUser));
        clearInterval(pollingInterval.current);
        history.push('/add-accounts');
      }
    }, pollingTime);

    return () => {
      console.log('SwapToPaddle', 'useEffect release', pollingInterval.current);
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [dispatch, pollingTime, pollingInterval]);

  useMountEffect(() => {
    initializePaddle();
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  });

  const startCheckout = async ({ commonIdentifier }) => {
    try {
      const { data: paddleData } = await paddleApi.subscribe({
        price: commonIdentifier,
        iap_subscription_id: account?.iapSubscription?.id,
        allow_existing: true,
      });
      const checkoutSettings = {
        transactionId: paddleData.transaction_id,
        customerAuthToken: paddleData.customer_auth_token,
        customer: {
          id: paddleData.customer_id,
        },
      };
      window.Paddle.Checkout.open(checkoutSettings);
    } catch (error) {
      console.error('Failed to initialize Paddle checkout:', error);
      dispatch(showError('Failed to initialize Paddle checkout'));
    }
  };

  const renderErrorMessage = () => {
    return (
      <>
        <div className="error-message">{errorMessage}</div>
      </>
    );
  };

  const handlePlanClick = plan => {
    onPlanSelect(plan);
    startCheckout(plan);
  };

  const renderPlans = () => {
    return [...historicalPlans, 'yearly', 'yearlyPlus'].map(planKey => {
      const plan = pricingPlans[planKey];
      return (
        <SubscriptionPlan
          key={planKey}
          plan={plan}
          isSelected={plan === selectedPlan}
          onClick={() => handlePlanClick(plan)}
          displayName={plan.displayName}
          price={plan.price}
          billingCycle={plan.billingCycle}
        />
      );
    });
  };

  return (
    <div className="swap-to-paddle">
      {renderPlans()}
      <div className="checkout-container" />

      {(isLoading || isFetching || provisioning) && (
        <span className="spinner-wrapper">
          <Spinner />
        </span>
      )}

      {errorMessage && renderErrorMessage()}
      <input id="artfulUserId" type="hidden" name="userId" value={account.id} />
      <input id="artfulEventId" type="hidden" name="eventId" value={eventId} />
    </div>
  );
};

SwapToPaddle.propTypes = {
  selectedPlan: PropTypes.object,
  isFetching: PropTypes.bool,
  account: PropTypes.object,
  handleSave: PropTypes.func,
  errorMessage: PropTypes.string,
  eventId: PropTypes.string,
  pricingPlans: PropTypes.object.isRequired,
  historicalPlans: PropTypes.array.isRequired,
  onPlanSelect: PropTypes.func.isRequired,
};

export default SwapToPaddle;
